<template>
    <div>
        <modal v-if="showJourneyFiltersEditor" @hidden="close()" show-on-mount>
            <template #modal-title>
                <div class="w-100">
                    <h3 class="d-inline-block mr-1">{{resource('JourneyComposer_GenericFiltersModal_Title')}}</h3>
                    <help-popover :content="resource('JourneyComposer_FilterModal_GenericFiltersModal_Explanation')" />
                </div>
            </template>
            
            <template #modal-body>
                <brick-filter-settings-editor
                    ref="editor"
                    
                    :includedCategoryFilters="[ 'Audience', 'NotAudience' ]"
                    :conditions="editableFiltersModel"
                    id-field="id"

                    :can-write="canWrite"
                    
                    :delete-group-confirmation-message="resource('JourneyComposer_GenericFiltersModal_DeleteGroupConfirmation')"
                    :delete-condition-confirmation-message="resource('JourneyComposer_GenericFiltersModal_DeleteConditionConfirmation')"

                    @conditions-changed="editableFiltersModel = $event"
                    @validation-state-changed="conditionsValid = $event"
                />
            </template>
            
            <template #modal-footer>
                <div class="row justify-content-end">
                    <div class="col-auto">
                        <button class="btn btn-outline-secondary btn-lg" @click="close()">
                            <i class="fa fa-ban"></i> {{resource('Button_Cancel')}}
                        </button>
                    </div>

                    <div class="col-auto">
                        <button class="btn btn-primary btn-lg" :class="{ disabled: $v.$invalid }" :disabled="!canWrite" @click="save()">
                            <i class="fa fa-save"></i> {{resource('Button_Save')}}
                        </button>
                    </div>
                </div>
            </template>
        </modal>
        
        <div class="text-center">
            <button class="btn btn-primary" @click="open()"><i class="far fa-list-alt mr-1" /> {{resource('JourneyBuilder_Menu_Button_OpenJourneyFilters')}}</button>
            <notification-circle :notification-count="activeFilters.length" />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import {Checkpoint, filterExclusionCheckpoint} from "@modules/JourneyBuilder/Bricks/Checkpoints";
import {mapState, mapMutations} from 'vuex';
import conditionExpansion from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Settings/Connection/condition-expansion';
import {conditionGroupValidations} from "@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Settings/Connection/validations";

import Modal from "@core/Components/Util/Modal";
import BrickFilterSettingsEditor from "@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Settings/Connection/BrickFilterSettingsEditor";
import HelpPopover from "@modules/JourneyBuilder/Components/Elements/HelpPopover";
import NotificationCircle from "@modules/JourneyBuilder/Components/Elements/NotificationCircle";

import resourcesMixin from "@core/Util/Mixins/resources";
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import injectJourneyMixin from '@modules/JourneyBuilder/Mixins/inject-journey';
import {validationMixin} from 'vuelidate';

export default {
    name: "menu-journey-filters",
    components: {
        HelpPopover,
        Modal,
        BrickFilterSettingsEditor,
        NotificationCircle
    },
    mixins: [resourcesMixin, permissionsMixin, injectJourneyMixin, validationMixin],
    validations: {
        conditionsValid: {
            isTrue: value => value === true
        },
        
        editableFiltersModel: conditionGroupValidations
    },
    data(){
        return {
            conditionsValid: true,
            
            editableJourneyConfig: {
                filters: []
            }
        }
    },
    computed: {
        ...mapState('journeyBuilder/composer', ['showJourneyFiltersEditor']),
        activeFilters(){
            return (this.journey.config && this.journey.config.filters) || [];
        },
        editableFiltersModel: {
            get(){
                return conditionExpansion.inflateGenericFilters(this.editableJourneyConfig.filters);
            },
            set(value){
                this.editableJourneyConfig.filters = conditionExpansion.deflateGenericFilters(value);
            }
        },
        /** @returns {JourneyBrickConnection[]} */
        connections() {
            return this.journey.bricks
                .flatMap(brick => brick.outgoingConnections);
        },

        /** @returns {JourneyBrickConnection[]} */
        connectionsWithFilterCheckpoint() {
            return this.connections.filter(connection => filterExclusionCheckpoint.isActiveForConnection(connection));
        }
    },
    watch: {
        showJourneyFiltersEditor(isOpen){
            if(!isOpen){
                return;
            }

            //Create an editable copy on open - saving needs to be an explicit action from the user
            const editableConfig = _.cloneDeep(this.journey.config);

            if(!editableConfig.filters){
                editableConfig.filters = [];
            }
            
            this.editableJourneyConfig = editableConfig;
        },
        activeFilters(newFilters, oldFilters){
            //Upon deactivation of our final filter; exclusion checkpoints serve no purpose anymore
            if(newFilters.length === 0){
                this.connectionsWithFilterCheckpoint.forEach(connection => filterExclusionCheckpoint.deactivateForConnection(this.journey, connection));
                this.connections.forEach(connection => Checkpoint.updateOrderForConnection(this.journey, connection));
                return;
            }
            
            const removedFilterIds = oldFilters.reduce((removedFilterIds, oldFilter) => {
                const filterIsRemoved = !newFilters.some(newFilter => newFilter.id === oldFilter.id);
                if(filterIsRemoved){
                    removedFilterIds.push(oldFilter.id);
                }
                
                return removedFilterIds;
            }, []);
            
            const createdFilterIds = newFilters.reduce((createdFilterIds, newFilter) => {
                const filterIsCreated = !oldFilters.some(oldFilter => oldFilter.id === newFilter.id);
                if(filterIsCreated){
                    createdFilterIds.push(newFilter.id);
                }
                
                return createdFilterIds;
            }, []);
            
            if(removedFilterIds.length === 0 && createdFilterIds.length === 0){
                return;
            }
            
            this.connectionsWithFilterCheckpoint.forEach((connection) => {
                const exclusionConfig = filterExclusionCheckpoint.readForConnection(connection);
                
                removedFilterIds.forEach(filterId => filterExclusionCheckpoint.toggleFilterExclusion(exclusionConfig, filterId, false));
                createdFilterIds.forEach(filterId => filterExclusionCheckpoint.toggleFilterExclusion(exclusionConfig, filterId, true));
            });

            this.connections.forEach(connection => Checkpoint.updateOrderForConnection(this.journey, connection));
        }
    },
    methods: {
        ...mapMutations('journeyBuilder/composer', ['toggleJourneyFiltersEditor']),

        open(){
            this.toggleJourneyFiltersEditor(true);
        },
        close(){
            this.toggleJourneyFiltersEditor(false);
        },
        save(){            
            if(this.$v.$invalid){
                this.$refs.editor.displayErrors();
                return;
            }
            
            this.journey.config = this.editableJourneyConfig;
            this.close();
        }
    }
}
</script>

<style scoped>
    .notification-container {
        top: -30px;
        left: -15px;
    }
</style>